import { useEffect, useRef, useState } from "react";

import { Cell, Pie, PieChart, ResponsiveContainer } from "recharts";

import { DashboardFilterType, ProductRatio } from "@/types/dashboard";
import { ValueType } from "@/types/bid";

import { getDashboardBidFilterToString } from "@/consts/Dashboard";

import { cn, formatToPercentString, formatKoreanCurrencyWithWon } from "@/libs/utils";

import { showToast } from "@/utils/toastUtils";

import { GetAgencyBudgets } from "@/services/Dashboard";

import DashboardCardHeader from "@/components/DashboardCardHeader";

import { ReactComponent as ChevronDown } from "@/assets/images/LNB/arrow-close.svg";

const COLORS = ["#344BFD", "#F4A79D"];

const renderCustomLabel = ({
  cx,
  cy,
  midAngle,
  innerRadius,
  outerRadius,
  percent,
}: any) => {
  const RADIAN = Math.PI / 180;
  const radius = innerRadius + (outerRadius - innerRadius) * 0.5;
  const x = cx + radius * Math.cos(-midAngle * RADIAN);
  const y = cy + radius * Math.sin(-midAngle * RADIAN);

  const displayPercent = formatToPercentString(percent);

  return (
    <g>
      <circle cx={x} cy={y} r={16} fill="#ECEAF8" strokeWidth={1} />
      <text
        x={x}
        y={y}
        fill="#000000"
        textAnchor="middle"
        dominantBaseline="middle"
        fontSize={12}
        fontWeight={700}
      >
        {displayPercent}
      </text>
    </g>
  );
};

type Props = {
  title: string;
  searchReady: boolean;
  filter: DashboardFilterType;
  selectedAgencies: ValueType[];
  onSearchComplete: () => void;
};

export default function DashboardDemandAgencyBudgetRatio({
  title,
  searchReady,
  filter,
  selectedAgencies,
  onSearchComplete,
}: Props) {
  const [isLoading, setIsLoading] = useState<boolean>(false);
  const [data, setData] = useState<ProductRatio[]>([]);
  const [checkedAgencies, setCheckedAgencies] = useState<string[]>([]);
  const [appliedAgencies, setAppliedAgencies] = useState<string[]>([]);
  const [isDropdownOpen, setIsDropdownOpen] = useState(false);
  const dropdownRef = useRef<HTMLDivElement>(null);

  // 외부 클릭 시 드롭다운 닫기
  useEffect(() => {
    const handleClickOutside = (event: MouseEvent) => {
      if (
        dropdownRef.current &&
        !dropdownRef.current.contains(event.target as Node)
      ) {
        setIsDropdownOpen(false);
      }
    };
    document.addEventListener("mousedown", handleClickOutside);
    return () => document.removeEventListener("mousedown", handleClickOutside);
  }, []);

  // selectedAgencies 변경 시 전체 선택 초기화
  useEffect(() => {
    const allIds = selectedAgencies.map((a) => a.value);
    setCheckedAgencies(allIds);
    setAppliedAgencies(allIds);
  }, [selectedAgencies]);

  const toggleAgency = (agencyId: string) => {
    setCheckedAgencies((prev) =>
      prev.includes(agencyId)
        ? prev.filter((id) => id !== agencyId)
        : [...prev, agencyId]
    );
  };

  const toggleAll = () => {
    if (checkedAgencies.length === selectedAgencies.length) {
      setCheckedAgencies([]);
    } else {
      setCheckedAgencies(selectedAgencies.map((a) => a.value));
    }
  };

  const onLoad = async (agencyIds?: string[]) => {
    try {
      setIsLoading(true);
      const params = getDashboardBidFilterToString(filter);
      const result = await GetAgencyBudgets(params);

      if (result.data.success) {
        const allData = result.data.pods[0].content.items.map((item) => ({
          label: item.agency_name,
          value: item.budget_amount,
          id: item.agency_id,
        }));

        // agencyIds가 있으면 필터링, 없으면 전체 (selectedAgencies 기준)
        const allAgencyIds = selectedAgencies.map((a) => a.value);
        const targetIds = agencyIds || allAgencyIds;
        if (targetIds.length === 0 || targetIds.length === selectedAgencies.length) {
          setData(allData);
        } else {
          const filtered = allData.filter((item) =>
            targetIds.includes(item.id)
          );
          setData(filtered);
        }
      } else {
        showToast({
          type: "error",
          message: `${title}을(를) 조회하는데 실패했습니다. ${result.data.error?.type}`,
        });
      }
    } catch (e) {
      showToast({
        type: "error",
        message: `${title}을(를) 조회하는데 실패했습니다. ${e}`,
      });
    } finally {
      setIsLoading(false);
      onSearchComplete();
    }
  };

  useEffect(() => {
    if (searchReady) {
      onLoad();
    }
  }, [searchReady]);

  const handleApply = () => {
    setAppliedAgencies(checkedAgencies);
    setIsDropdownOpen(false);
    onLoad(checkedAgencies);
  };

  const getDropdownLabel = () => {
    if (appliedAgencies.length === 0) return "선택 없음";
    if (appliedAgencies.length === selectedAgencies.length) return "전체";
    return `${appliedAgencies.length}개 선택`;
  };

  return (
    <DashboardCardHeader
      title={title}
      isLoading={isLoading}
      rightElement={
        <div className="relative" ref={dropdownRef}>
          <button
            onClick={() => setIsDropdownOpen(!isDropdownOpen)}
            className={cn(
              "h-8 px-3 border border-gray-D rounded text-xs cursor-pointer flex items-center gap-2",
              "focus:outline-none focus:ring-1 focus:ring-blue-500 bg-white"
            )}
          >
            <span>{getDropdownLabel()}</span>
            <ChevronDown
              className={cn(
                "w-3 h-3 transition-transform",
                isDropdownOpen && "rotate-180"
              )}
            />
          </button>
          {isDropdownOpen && (
            <div className="absolute right-0 top-full mt-1 w-48 bg-white border border-gray-D rounded shadow-lg z-10">
              <div className="max-h-40 overflow-y-auto">
                <label className="flex items-center gap-2 px-3 py-2 hover:bg-gray-E cursor-pointer border-b border-gray-E">
                  <input
                    type="checkbox"
                    checked={checkedAgencies.length === selectedAgencies.length}
                    onChange={toggleAll}
                    className="w-4 h-4 min-w-[16px] min-h-[16px] flex-shrink-0"
                  />
                  <span className="text-xs font-semibold">전체</span>
                </label>
                {selectedAgencies.map((agency) => (
                  <label
                    key={agency.value}
                    className="flex items-center gap-2 px-3 py-2 hover:bg-gray-E cursor-pointer"
                  >
                    <input
                      type="checkbox"
                      checked={checkedAgencies.includes(agency.value)}
                      onChange={() => toggleAgency(agency.value)}
                      className="w-4 h-4 min-w-[16px] min-h-[16px] flex-shrink-0"
                    />
                    <span className="text-xs truncate">{agency.label}</span>
                  </label>
                ))}
              </div>
              <div className="border-t border-gray-E p-2">
                <button
                  onClick={handleApply}
                  className="w-full py-1.5 bg-dark-red text-white text-xs font-semibold rounded hover:bg-dark-red-80"
                >
                  적용
                </button>
              </div>
            </div>
          )}
        </div>
      }
    >
      <ResponsiveContainer width="100%" height={230}>
        <PieChart margin={{ top: 0, right: 0, bottom: 0, left: 0 }}>
          <Pie
            data={data}
            cx="50%"
            cy="50%"
            innerRadius={60}
            outerRadius={90}
            labelLine={false}
            label={renderCustomLabel}
            dataKey="value"
          >
            {data.map((entry, index) => (
              <Cell
                key={`cell-${index}`}
                fill={COLORS[index % COLORS.length]}
              />
            ))}
          </Pie>
        </PieChart>
      </ResponsiveContainer>

      <div className="flex justify-center gap-6 text-sm mt-4">
        {data.map((entry, index) => (
          <div
            key={entry.label}
            className="flex items-center gap-1 text-sm font-semibold"
          >
            <span
              className="w-3 h-3 rounded-full inline-block"
              style={{ backgroundColor: COLORS[index % COLORS.length] }}
            />
            <span className="text-gray-A">{entry.label}</span>
            <span className="text-gray-B">{formatKoreanCurrencyWithWon(entry.value)}</span>
          </div>
        ))}
      </div>
    </DashboardCardHeader>
  );
}
